import { createContext, useContext, useState } from 'react';
import { useAuth } from './AuthContext.jsx';
import { getCookie } from '../utils/helpers.js';
import { TOKEN, getApiBaseUrl } from '@constants/constants';

const EmployeeContext = createContext();

export const useEmployees = () => {
  const context = useContext(EmployeeContext);
  if (context === undefined) {
    throw new Error('useEmployees must be used within an EmployeeProvider');
  }
  return context;
};

export const EmployeeProvider = ({ children }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  // Refresh employees from backend
  const refreshEmployees = async () => {
    if (!isAuthenticated()) return;
    
    const token = getCookie(TOKEN);
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/employees`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (error) {
      console.error('Error refreshing employees:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get all employees
  const getAllEmployees = () => employees;

  // Get employee by ID
  const getEmployeeById = (id) => {
    if (!id) return null;
    return employees.find(emp => 
      emp.id === id || 
      emp.employee_id === id || 
      emp.employeeId === id ||
      emp.id === parseInt(id) ||
      emp.employee_id === parseInt(id) ||
      emp.employeeId === parseInt(id)
    );
  };

  // Add new employee
  const addEmployee = async (employeeData) => {
    setLoading(true);
    try {
      if (isAuthenticated()) {
        const token = getCookie(TOKEN);
        if (!token) {
          throw new Error('No authentication token found');
        }

        const backendData = {
          first_name: employeeData.firstName,
          last_name: employeeData.lastName,
          email: employeeData.email,
          mobile_number: employeeData.phone || null,
          date_of_birth: employeeData.dateOfBirth || null,
          designation: employeeData.role || employeeData.title || null,
          parent_department: employeeData.parentDepartment || null,
          department: employeeData.department || null,
          city: employeeData.location || null,
          employment_type: employeeData.employmentType || null,
          date_of_joining: employeeData.hireDate || null,
          employee_id: employeeData.employeeId,
          reporting_manager_name: employeeData.managerName || null,
          billing_rate_per_hour: employeeData.billingRate && employeeData.billingRate.trim() 
            ? parseFloat(employeeData.billingRate) 
            : null
        };

        const response = await fetch(`${getApiBaseUrl()}/api/employees`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(backendData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create employee');
        }

        const data = await response.json();
        await refreshEmployees();
        
        return { success: true, employee: data.employee };
      }
      return { success: false, error: 'Not authenticated' };
    } catch (error) {
      console.error('Error adding employee:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Update employee
  const updateEmployee = async (id, updates) => {
    setLoading(true);
    try {
      if (isAuthenticated()) {
        const token = getCookie(TOKEN);
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await fetch(`${getApiBaseUrl()}/api/employees/${id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updates)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update employee');
        }

        const data = await response.json();
        
        setEmployees(prev => 
          prev.map(emp => {
            const empId = emp.id || emp.employee_id || emp.employeeId;
            const currentId = parseInt(id);
            if (empId === currentId) {
              return { ...emp, ...data.employee };
            }
            return emp;
          })
        );
        
        return { success: true, employee: data.employee };
      }
      return { success: false, error: 'Not authenticated' };
    } catch (error) {
      console.error('Error updating employee:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Delete employee
  const deleteEmployee = async (id) => {
    setLoading(true);
    try {
      if (isAuthenticated()) {
        const token = getCookie(TOKEN);
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await fetch(`${getApiBaseUrl()}/api/employees/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete employee');
        }

        setEmployees(prev => prev.filter(emp => {
          const empId = emp.id || emp.employee_id || emp.employeeId;
          const deleteId = parseInt(id);
          return empId !== deleteId;
        }));
        
        return { success: true };
      }
      return { success: false, error: 'Not authenticated' };
    } catch (error) {
      console.error('Error deleting employee:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Search and filter employees
  const searchEmployees = (query, filters = {}) => {
    let filtered = employees || [];

    if (query && query.trim()) {
      const searchLower = query.toLowerCase().trim();
      filtered = filtered.filter(emp => {
        const firstName = (emp.first_name || emp.firstName || '').toString();
        const lastName = (emp.last_name || emp.lastName || '').toString();
        const email = (emp.email || '').toString();
        const employeeId = (emp.employee_id || emp.employeeId || emp.id || '').toString();
        
        return firstName.toLowerCase().includes(searchLower) ||
          lastName.toLowerCase().includes(searchLower) ||
          email.toLowerCase().includes(searchLower) ||
          employeeId.toLowerCase().includes(searchLower) ||
          `${firstName} ${lastName}`.toLowerCase().includes(searchLower);
      });
    }

    if (filters.department?.length > 0) {
      filtered = filtered.filter(emp => 
        emp.department && filters.department.includes(emp.department)
      );
    }
    
    if (filters.location?.length > 0) {
      filtered = filtered.filter(emp => {
        const location = emp.city || emp.location;
        return location && filters.location.includes(location);
      });
    }

    return filtered;
  };

  // Get statistics
  const getStats = () => {
    const total = employees.length;
    const active = employees.filter(emp => 
      (emp.employee_status || emp.status || 'Active') === 'Active'
    ).length;
    
    return {
      total,
      active,
      inactive: total - active
    };
  };

  // Get work locations
  const getWorkLocations = () => {
    const locations = new Set();
    employees.forEach(emp => {
      const location = emp.city || emp.location;
      if (location) locations.add(location);
    });
    return Array.from(locations).sort();
  };

  // Get department overview
  const getDepartmentOverview = () => {
    const departments = {};
    employees.forEach(emp => {
      const dept = emp.department || 'Unknown';
      departments[dept] = (departments[dept] || 0) + 1;
    });
    return departments;
  };

  const value = {
    employees,
    loading,
    refreshEmployees,
    getAllEmployees,
    getEmployeeById,
    addEmployee,
    updateEmployee,
    deleteEmployee,
    searchEmployees,
    getStats,
    getWorkLocations,
    getDepartmentOverview
  };

  return (
    <EmployeeContext.Provider value={value}>
      {children}
    </EmployeeContext.Provider>
  );
};

export default EmployeeContext;
